/**
 * The layouts of docs/FORMAT.md §2: the three head bytes, the message envelope, the additional data, and the IV of a chunk.
 * Bytes in, bytes out; no key is touched here.
 */
import type { Role } from '@/api/types';

import { fromBase64Url, toBase64Url } from './base64url';
import {
  ENVELOPE_HEAD_BYTES,
  FILE_CHUNK_LABEL,
  FILE_NONCE_PATTERN,
  FLAGS_KNOWN,
  FLAG_DELETION,
  FLAG_LAST_CHUNK,
  FLAG_PADDED,
  IV_BYTES,
  MESSAGE_HEAD_BYTES,
  MESSAGE_ID_PATTERN,
  MESSAGE_LABEL,
  PART_DELETION,
  ROLE_BYTE,
  SENDER_BYTES,
  SEQ_BYTES,
  SUITE_AES_GCM,
  TAG_BYTES,
  VERSION,
} from './labels';
import { concat, DecryptError, UnsupportedVersionError } from './primitives';

const encoder = new TextEncoder();

export function u16(value: number): Uint8Array<ArrayBuffer> {
  return Uint8Array.of((value >> 8) & 255, value & 255);
}

export function u32(value: number): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(new ArrayBuffer(4));
  new DataView(out.buffer).setUint32(0, value);
  return out;
}

/** `u16(len) ‖ bytes`: a field that cannot run into the next one. */
export function lp(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  if (bytes.length > 0xffff) throw new DecryptError('A field is too long.');
  return concat(u16(bytes.length), bytes);
}

/** version ‖ suite ‖ flags, checked: another version or suite is a different Bridge, an unknown flag is no envelope of ours. */
export function checkHead(data: Uint8Array, minimum: number): { flags: number } {
  if (data.length < ENVELOPE_HEAD_BYTES) throw new DecryptError('Invalid ciphertext length: too short.');
  if (data[0] !== VERSION) throw new UnsupportedVersionError();
  if (data[1] !== SUITE_AES_GCM) throw new UnsupportedVersionError('This was made with an encryption suite this version of Bridge does not know. Update Bridge and open the link again.');
  const flags = data[2] ?? 0;
  if ((flags & ~FLAGS_KNOWN) !== 0) throw new DecryptError('Invalid ciphertext: unknown flags.');
  if (data.length < minimum) throw new DecryptError('Invalid ciphertext length: too short.');
  return { flags };
}

// --------------------------------------------------------------- messages

/** Who wrote a message and in which place of that device's run (docs/FORMAT.md §5). */
export interface Position {
  readonly sender: Uint8Array<ArrayBuffer>;
  readonly seq: number;
}

export type MessagePart = 'text' | 'file-header' | 'deletion';

export const PART_BYTE: Record<MessagePart, number> = { text: 1, 'file-header': 2, deletion: PART_DELETION };

/** The flags a message envelope of this part must carry: padded, and for a deletion mark also the deletion bit. */
export function messageFlags(part: MessagePart): number {
  return part === 'deletion' ? FLAG_PADDED | FLAG_DELETION : FLAG_PADDED;
}

export interface MessageBinding {
  readonly bridgeId: string;
  readonly messageId: string;
  readonly from: Role;
}

/** The additional data of a message (docs/FORMAT.md §2.1). */
export function messageAad(binding: MessageBinding, part: MessagePart, flags: number, position: Position): Uint8Array<ArrayBuffer> {
  if (!MESSAGE_ID_PATTERN.test(binding.messageId)) throw new DecryptError('Invalid message id.');
  return concat(
    encoder.encode(MESSAGE_LABEL),
    Uint8Array.of(0, VERSION, SUITE_AES_GCM, flags),
    lp(encoder.encode(binding.bridgeId)),
    lp(encoder.encode(binding.messageId)),
    Uint8Array.of(ROLE_BYTE[binding.from], PART_BYTE[part]),
    position.sender,
    u32(position.seq),
  );
}

export interface MessageEnvelope {
  readonly flags: number;
  readonly position: Position;
  readonly iv: Uint8Array<ArrayBuffer>;
  /** Ciphertext and tag. */
  readonly sealed: Uint8Array<ArrayBuffer>;
}

export function buildMessageEnvelope(flags: number, position: Position, iv: Uint8Array<ArrayBuffer>, sealed: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> {
  return concat(Uint8Array.of(VERSION, SUITE_AES_GCM, flags), position.sender, u32(position.seq), iv, sealed);
}

/** The first bytes of a message envelope, read without a key. Nothing here is authenticated until the message is opened. */
export function readMessageHead(data: Uint8Array<ArrayBuffer>): { flags: number; position: Position } {
  const { flags } = checkHead(data, MESSAGE_HEAD_BYTES + TAG_BYTES);
  const sender = data.slice(ENVELOPE_HEAD_BYTES, ENVELOPE_HEAD_BYTES + SENDER_BYTES);
  const seq = new DataView(data.buffer, data.byteOffset + ENVELOPE_HEAD_BYTES + SENDER_BYTES, SEQ_BYTES).getUint32(0);
  return { flags, position: { sender, seq } };
}

export function parseMessageEnvelope(encoded: string): MessageEnvelope {
  let data: Uint8Array<ArrayBuffer>;
  try {
    data = fromBase64Url(encoded);
  } catch {
    throw new DecryptError('Invalid ciphertext encoding.');
  }
  // one envelope, one spelling: the trailing bits of the last character are zero (docs/FORMAT.md)
  if (toBase64Url(data) !== encoded) throw new DecryptError('Invalid ciphertext encoding.');
  const { flags, position } = readMessageHead(data);
  const ivAt = ENVELOPE_HEAD_BYTES + SENDER_BYTES + SEQ_BYTES;
  return { flags, position, iv: data.slice(ivAt, ivAt + IV_BYTES), sealed: data.slice(MESSAGE_HEAD_BYTES) };
}

// ----------------------------------------------------------------- chunks

/** The additional data of a chunk (docs/FORMAT.md §2.2). */
export function chunkAad(nonce: string, index: number, count: number, flags: number): Uint8Array<ArrayBuffer> {
  if (!FILE_NONCE_PATTERN.test(nonce)) throw new DecryptError('Invalid file header.');
  return concat(encoder.encode(FILE_CHUNK_LABEL), Uint8Array.of(0, VERSION, SUITE_AES_GCM, flags), lp(encoder.encode(nonce)), u32(index), u32(count));
}

/** The IV of a chunk: seven zero bytes, the index, and 1 for the last chunk. */
export function chunkIv(index: number, last: boolean): Uint8Array<ArrayBuffer> {
  return concat(new Uint8Array(7), u32(index), Uint8Array.of(last ? 1 : 0));
}

export function chunkFlags(last: boolean): number {
  return last ? FLAG_LAST_CHUNK : 0;
}
